<script setup>
import {computed} from "vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import CountryDataRow from "@/components/reporting/partials/CountryDataRow.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import useTrafficStore from "@/composables/useTrafficStore";
import useAccountMissing from "@/composables/useAccountMissing";
import Reporting from "@/services/reporting";
import {useQueries} from "@tanstack/vue-query";
import useQueriesState from "@/composables/useQueriesState";
import ConnectAccount from "@/components/reporting/partials/ConnectAccount.vue";
import ErrorBlock from "@/components/reporting/partials/ErrorBlock.vue";
import ReportingEmpty from "@/components/reporting/partials/ReportingEmpty.vue";
import useOrderBy from "@/composables/useOrderBy";

const {orderBy, orderDirection, orderChanged, orderData} = useOrderBy('views');

const {fromDate, toDate, compareFromDate, compareToDate} = useTrafficStore();

const {isAccountMissing, setIsAccountMissing, checkResponseForAccountMissing} = useAccountMissing();

const fetchCountries = async (fromDate, toDate) => {
  if (!fromDate || !toDate) return null;
  setIsAccountMissing(false);
  try {
    return (await Reporting.fetchCountries(fromDate, toDate)).data.data;
  } catch (e) {
    checkResponseForAccountMissing(e.response);
    throw e;
  }
}

const results = useQueries({
  queries: [
    {
      queryKey: ['reporting-countries', 'reporting', fromDate, toDate],
      queryFn: async () => fetchCountries(fromDate.value, toDate.value),
    },
    {
      queryKey: ['reporting-countries', 'reporting', compareFromDate, compareToDate],
      queryFn: async () => fetchCountries(compareFromDate.value, compareToDate.value),
    },
  ],
});

const {data, comparedData, isLoading: loading, isError} = useQueriesState(results);

const orderedData = computed(() => orderData(data.value).slice(0, 5));

</script>

<template>
  <LoadingBlock v-if="loading" />
  <ConnectAccount v-else-if="isAccountMissing" />
  <ErrorBlock v-else-if="isError" />
  <ReportingEmpty v-else-if="!loading && !data.length" />
  <DataTable v-else>
    <TableHeaderRow class="tw-px-0 tw-mx-0">
      <TableHeaderCell
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="country"
        class="tw-col-span-6"
        @order-changed="orderChanged"
      >
        Country
      </TableHeaderCell>

      <TableHeaderCell
        :order-by="orderBy"
        :order-direction="orderDirection"
        name="value"
        class="tw-col-span-6 tw-justify-end"
        @order-changed="orderChanged"
      >
        Views
      </TableHeaderCell>
    </TableHeaderRow>
    <TableBody class="!tw-py-0 !tw-border-b-0">
      <CountryDataRow
        v-for="(row) in orderedData"
        :key="row.country"
        :country-code="row.countryCode"
        :label="row.country"
        :value="row.value"
        :compare-value="comparedData?.find(compare => compare.country === row.country)?.value"
      />
    </TableBody>
  </DataTable>
</template>